import * as React from 'react';

import SearchIcon from '@mui/icons-material/Search';
import VerifiedIcon from '@mui/icons-material/Verified';
import {
  Box,
  FormControlLabel,
  InputAdornment,
  LinearProgress,
  Radio,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import styles from './ParentReportPicker.module.css';

export interface ParentReportOption {
  jobId: string;
  reportId: string;
  startedAt: string;
  clientName: string;
  projectName: string;
  targetGroupName: string;
  workspaceName?: string;
  passedQa: boolean;
}

export interface ParentReportPickerProps {
  reports: ParentReportOption[];
  selectedJobId: string | null;
  onSelect: (report: ParentReportOption) => void;
  loading?: boolean;
  error?: string;
  /** Explains why a list may be shorter than expected (e.g. M&M needs an optimization run). */
  helperText?: string;
}

const PAGE_SIZE = 8;

const formatDate = (iso: string) => {
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? iso
    : date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

/** Searchable, paginated single-select table of previously run reports. */
const ParentReportPicker: React.FC<ParentReportPickerProps> = ({
  reports,
  selectedJobId,
  onSelect,
  loading,
  error,
  helperText,
}) => {
  const [query, setQuery] = React.useState('');
  const [qaOnly, setQaOnly] = React.useState(true);
  const [page, setPage] = React.useState(0);

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    return reports.filter(
      (r) =>
        (!qaOnly || r.passedQa || r.jobId === selectedJobId) &&
        (!q ||
          [r.clientName, r.projectName, r.targetGroupName, r.workspaceName, r.jobId]
            .filter(Boolean)
            .some((v) => v.toLowerCase().includes(q))),
    );
  }, [reports, query, qaOnly, selectedJobId]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount - 1);
  const selected = selectedJobId ? reports.find((r) => r.jobId === selectedJobId) : undefined;

  return (
    <Box className={styles.picker}>
      <Box className={styles.toolbar}>
        <TextField
          size="small"
          placeholder="Search client, project, target group"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setPage(0);
          }}
          inputProps={{ 'aria-label': 'Search reports' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          className={styles.search}
        />
        <FormControlLabel
          control={
            <Switch
              checked={qaOnly}
              onChange={(e) => {
                setQaOnly(e.target.checked);
                setPage(0);
              }}
            />
          }
          label="QA passed only"
        />
      </Box>
      {helperText && (
        <Typography variant="body2" color="text.secondary">
          {helperText}
        </Typography>
      )}
      {selected && (
        <Typography variant="body2" className={styles.selected} aria-live="polite">
          Selected: <strong>{selected.projectName}</strong> · {selected.targetGroupName} ·{' '}
          {formatDate(selected.startedAt)}
        </Typography>
      )}
      <TableContainer className={styles.table}>
        {loading && <LinearProgress aria-label="Loading reports" />}
        <Table size="small" aria-label="Reports">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              <TableCell>Project</TableCell>
              <TableCell>Client</TableCell>
              <TableCell>Target group</TableCell>
              <TableCell>Run</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE).map((r) => {
              const isSelected = !!selectedJobId && r.jobId === selectedJobId;
              return (
                <TableRow
                  key={r.reportId}
                  hover
                  selected={isSelected}
                  onClick={() => onSelect(r)}
                  className={styles.row}
                >
                  <TableCell padding="checkbox">
                    <Radio
                      checked={isSelected}
                      onChange={() => onSelect(r)}
                      inputProps={{ 'aria-label': `${r.projectName} – ${r.targetGroupName}` }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box className={styles.project}>
                      <span>{r.projectName}</span>
                      {r.passedQa && (
                        <Tooltip title="Passed QA">
                          <VerifiedIcon color="success" fontSize="inherit" aria-label="Passed QA" />
                        </Tooltip>
                      )}
                    </Box>
                    {r.workspaceName && (
                      <Typography variant="caption" color="text.secondary">
                        {r.workspaceName}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{r.clientName}</TableCell>
                  <TableCell>{r.targetGroupName}</TableCell>
                  <TableCell>{formatDate(r.startedAt)}</TableCell>
                </TableRow>
              );
            })}
            {!loading && filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography variant="body2" color="text.secondary" className={styles.empty}>
                    {reports.length === 0
                      ? 'No eligible reports in this workspace.'
                      : 'No reports match your search.'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {filtered.length > PAGE_SIZE && (
        <TablePagination
          component="div"
          count={filtered.length}
          page={currentPage}
          rowsPerPage={PAGE_SIZE}
          rowsPerPageOptions={[PAGE_SIZE]}
          onPageChange={(_, p) => setPage(p)}
        />
      )}
      {error && (
        <Typography variant="body2" color="error" role="alert">
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default ParentReportPicker;
